import React, { useEffect, useState } from 'react'
import { useNavigate, Navigate } from 'react-router-dom'
import { TopNavigation } from '@/components/TopNavigation'
import { ColorSquare } from '@/components/ColorSquare'
import { TestDeviceIcon } from '@/components/TestDeviceIcon'
import { useAuth } from '@/hooks/useAuth'
import { DeviceService, UserProfileHelper } from '@lura/shared'
import type { ClinicianDevice } from '@lura/shared'

export const ClinicianDevicesPage: React.FC = () => {
  const { user, signOut } = useAuth()
  const navigate = useNavigate()
  const [devices, setDevices] = useState<ClinicianDevice[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [sortBy, setSortBy] = useState<
    'serial' | 'date-newest' | 'date-oldest'
  >('serial')

  const fetchDevices = async () => {
    if (!user?.profile?.id) return

    try {
      setLoading(true)
      setError(null)
      const data = await DeviceService.getClinicianDevices(user.profile.id)
      setDevices(data)
    } catch (err) {
      console.error('Error fetching devices:', err)
      setError(err instanceof Error ? err.message : 'Failed to load devices')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchDevices()
  }, [user?.profile?.id])

  const handleSignOut = async () => {
    try {
      await signOut()
      navigate('/login')
    } catch (error) {
      console.error('Sign out error:', error)
    }
  }

  // Filter and sort devices
  const filteredAndSortedDevices = React.useMemo(() => {
    let filtered = devices

    // Apply search filter
    if (searchQuery) {
      const searchLower = searchQuery.toLowerCase()
      filtered = devices.filter(
        device =>
          device.serial_number.toLowerCase().includes(searchLower) ||
          device.study_title?.toLowerCase().includes(searchLower)
      )
    }

    // Apply sorting
    const sorted = [...filtered]
    if (sortBy === 'serial') {
      sorted.sort((a, b) => a.serial_number.localeCompare(b.serial_number))
    } else if (sortBy === 'date-newest') {
      sorted.sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      )
    } else if (sortBy === 'date-oldest') {
      sorted.sort(
        (a, b) =>
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      )
    }

    return sorted
  }, [devices, searchQuery, sortBy])

  // Clinician access check
  if (!UserProfileHelper.isClinician(user)) {
    return <Navigate to="/patients" replace />
  }

  return (
    <div className="min-h-screen bg-off-white">
      <TopNavigation
        currentPage="Devices"
        onSignOut={handleSignOut}
        user={user}
      />

      <main className="max-w-7xl mx-auto py-8 px-6">
        <div className="px-4 pb-6 sm:px-0">
          {/* Page Header */}
          <div className="mb-8">
            <h2
              className="text-5xl font-normal text-dark-blue"
              style={{ fontFamily: 'Family' }}
            >
              Devices
            </h2>
          </div>

          {/* Search and Sort */}
          <div className="mb-6 space-y-4">
            <div className="flex gap-2">
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search devices..."
                className="flex-1 px-4 py-2.5 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-dark-blue focus:border-transparent"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="px-6 py-2.5 border border-gray-300 rounded-2xl hover:bg-gray-50 transition-colors"
                >
                  Clear
                </button>
              )}
            </div>

            {/* Sort Buttons */}
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-gray-700">
                Sort by:
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setSortBy('serial')}
                  className={`px-4 py-2 rounded-2xl text-sm font-medium transition-colors ${
                    sortBy === 'serial'
                      ? 'bg-dark-blue text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Serial Number
                </button>
                <button
                  onClick={() => setSortBy('date-newest')}
                  className={`px-4 py-2 rounded-2xl text-sm font-medium transition-colors ${
                    sortBy === 'date-newest'
                      ? 'bg-dark-blue text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Newest First
                </button>
                <button
                  onClick={() => setSortBy('date-oldest')}
                  className={`px-4 py-2 rounded-2xl text-sm font-medium transition-colors ${
                    sortBy === 'date-oldest'
                      ? 'bg-dark-blue text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Oldest First
                </button>
              </div>
            </div>
          </div>

          {/* Error State */}
          {error && (
            <div className="mb-6 rounded-3xl bg-red-50 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-red-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-red-800">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
            </div>
          )}

          {/* Devices Table */}
          {!loading && !error && (
            <div className="bg-white shadow-sm rounded-3xl overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">
                  My Devices
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  Devices available through your assigned studies
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-highlight-green">
                    <tr>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-dark-blue uppercase tracking-wider"
                      >
                        Device
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-dark-blue uppercase tracking-wider"
                      >
                        Study
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-dark-blue uppercase tracking-wider"
                      >
                        Firmware
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-dark-blue uppercase tracking-wider"
                      >
                        Created Date
                      </th>
                      <th scope="col" className="relative px-6 py-3">
                        <span className="sr-only">Manage</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredAndSortedDevices.length === 0 ? (
                      <tr>
                        <td
                          colSpan={5}
                          className="px-6 py-12 text-center text-sm text-gray-500"
                        >
                          {searchQuery
                            ? 'No devices found matching your search'
                            : 'No devices assigned to you yet. Contact an administrator to get devices assigned.'}
                        </td>
                      </tr>
                    ) : (
                      filteredAndSortedDevices.map(device => (
                        <tr
                          key={device.id}
                          onClick={() =>
                            navigate(`/clinician/devices/${device.id}`)
                          }
                          className="hover:bg-gray-50 cursor-pointer transition-colors"
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-3">
                              {device.study_color && (
                                <ColorSquare
                                  color={device.study_color}
                                  size="md"
                                />
                              )}
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-gray-900">
                                  {device.serial_number}
                                </span>
                                {device.is_test_device && <TestDeviceIcon />}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {device.study_title || (
                                <span className="text-gray-400">
                                  Not assigned to study
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm text-gray-500">
                              {device.firmware_version || '—'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-500">
                              {new Date(device.created_at).toLocaleDateString(
                                'en-US',
                                {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric'
                                }
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex justify-end">
                              <svg
                                className="w-5 h-5 text-gray-400"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M9 5l7 7-7 7"
                                />
                              </svg>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  )
}
